import React, { useMemo } from "../../__compat__/react/main.entry.mjs.mjs";
import styled, { ThemeProvider as StyledThemeProvider, } from "../../__compat__/styled-components/main.entry.mjs.mjs";
import { isPlainObject } from "../../__compat__/lodash/main.entry.mjs.mjs";
import { spacingLabels, typographyVariantLabels, } from "../../themes/index.mjs.mjs";
import { responsiveStyles } from "../../utils/responsiveStyles.mjs.mjs";
import { cssPostfix } from "../../utils/cssPostfix.mjs.mjs";
const postfix = (propertyName) => `--${propertyName}-${cssPostfix}`;
const withUnit = (value, unit) => value !== undefined ? value + unit : value;
function generateCustomPropertyNamesFromTheme(obj, parentKey) {
    const newObj = Object.create(null);
    for (const key of Object.keys(obj)) {
        const newKey = parentKey ? [parentKey, key].join('-') : key;
        if (isPlainObject(obj[key])) {
            newObj[key] = generateCustomPropertyNamesFromTheme(obj[key], newKey);
        }
        else {
            newObj[key] = postfix(newKey);
        }
    }
    return newObj;
}
function assignDeepFlat(keys, values) {
    return Object.keys(keys).reduce((acc, key) => {
        if (isPlainObject(keys[key])) {
            return { ...acc, ...assignDeepFlat(keys[key], values[key]) };
        }
        else {
            return { ...acc, [keys[key]]: values[key] };
        }
    }, {});
}
export const CustomPropertyProvider = styled.div.withConfig({
    // stylelint already intents to parse the config as CSS
    // stylelint-disable-next-line property-no-unknown
    shouldForwardProp: (prop) => ['children', 'className'].includes(prop),
})(({ typography, spacing, breakpoints, properties }) => responsiveStyles({
    display: 'contents',
    ...properties,
    ...typographyVariantLabels.reduce((rest, variantLabel) => ({
        ...rest,
        [postfix(`typography-${variantLabel}-fontFamily`)]: (breakpointLabel) => { var _a; return (_a = typography[variantLabel][breakpointLabel]) === null || _a === void 0 ? void 0 : _a.fontFamily; },
        [postfix(`typography-${variantLabel}-fontSize`)]: (breakpointLabel) => {
            var _a;
            return withUnit((_a = typography[variantLabel][breakpointLabel]) === null || _a === void 0 ? void 0 : _a.fontSize, 'px');
        },
        [postfix(`typography-${variantLabel}-fontStretch`)]: (breakpointLabel) => {
            var _a;
            return withUnit((_a = typography[variantLabel][breakpointLabel]) === null || _a === void 0 ? void 0 : _a.fontStretch, '%');
        },
        [postfix(`typography-${variantLabel}-lineHeight`)]: (breakpointLabel) => {
            var _a;
            return withUnit((_a = typography[variantLabel][breakpointLabel]) === null || _a === void 0 ? void 0 : _a.lineHeight, 'px');
        },
    }), {}),
    ...spacingLabels.reduce((rest, spacingLabel) => ({
        ...rest,
        [postfix(`space-${spacingLabel}`)]: (breakpointLabel) => withUnit(spacing[spacingLabel][breakpointLabel], 'px'),
    }), {}),
}, breakpoints));
export const ThemeProvider = ({ theme, children }) => {
    const { breakpoints, name, spacing, typography, ...restTheme } = theme;
    const themeFragmentVariablesTree = useMemo(() => generateCustomPropertyNamesFromTheme(restTheme), [restTheme]);
    const themeFragmentCustomProperties = useMemo(() => assignDeepFlat(themeFragmentVariablesTree, restTheme), [restTheme, themeFragmentVariablesTree]);
    const themeWithVariables = useMemo(() => ({
        ...themeFragmentVariablesTree,
        breakpoints,
        name,
        responsive: {
            spacing: spacingLabels.reduce((rest, spacingLabel) => ({
                ...rest,
                [spacingLabel]: postfix(`space-${spacingLabel}`),
            }), {}),
            typography: typographyVariantLabels.reduce((rest, variantLabel) => ({
                ...rest,
                [variantLabel]: {
                    fontFamily: postfix(`typography-${variantLabel}-fontFamily`),
                    fontSize: postfix(`typography-${variantLabel}-fontSize`),
                    fontStretch: postfix(`typography-${variantLabel}-fontStretch`),
                    lineHeight: postfix(`typography-${variantLabel}-lineHeight`),
                },
            }), {}),
        },
    }), [breakpoints, name, themeFragmentVariablesTree]);
    return (React.createElement(CustomPropertyProvider, { properties: themeFragmentCustomProperties, ...theme },
        React.createElement(StyledThemeProvider, { theme: themeWithVariables }, children)));
};
//# sourceMappingURL=ThemeProvider.mjs.mjs.map